/**
 * Quick view: the magnifier on a product card opens the product in a popup — photos, price,
 * options, quantity, Add to cart / Buy now, wishlist and compare — without leaving the page.
 *
 * The popup's content is sections/quick-view.liquid, rendered for the product by Shopify through
 * the Section Rendering API (/products/<handle>?sections=quick-view), so price and stock are live.
 * Its buy box has the product page's hooks: assets/product-buy-box.js runs the options and
 * assets/cart-sidebar.js does the adding. Adding opens the cart panel on top of the popup, and
 * closing the panel comes back to it.
 *
 * Hooks: [data-quick-view][data-product-handle]   the card buttons (snippets/product-card*.liquid)
 * API:   window.BEQuickView = { open(handle, opener), close() }
 */
(function () {
	'use strict';

	if (window.BEQuickView) return;

	var SECTION = 'quick-view';
	var CACHE_MS = 60 * 1000; // reopening the same product within a minute skips the request
	var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

	var modal = null;
	var dialog = null;
	var body = null;
	var lastOpener = null;
	var currentHandle = null;
	var request = 0;
	var cache = {}; // handle -> { html, at }

	function all(context, selector) {
		return Array.prototype.slice.call(context.querySelectorAll(selector));
	}

	/* ───────────────────────── shell ───────────────────────── */

	function build() {
		if (modal) return;

		modal = document.createElement('div');
		modal.className = 'be-qv-modal';
		modal.setAttribute('aria-hidden', 'true');
		modal.setAttribute('inert', '');
		modal.innerHTML =
			'<div class="be-qv-modal__overlay" data-quick-view-close></div>' +
			'<div class="be-qv-modal__dialog" role="dialog" aria-modal="true" aria-label="Quick view" tabindex="-1">' +
			'<button type="button" class="be-qv-modal__close" data-quick-view-close aria-label="Close quick view">' +
			'<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
			'<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>' +
			'</button>' +
			'<div class="be-qv-modal__body" data-quick-view-body></div>' +
			'</div>';
		document.body.appendChild(modal);

		dialog = modal.querySelector('[role="dialog"]');
		body = modal.querySelector('[data-quick-view-body]');

		modal.addEventListener('click', function (event) {
			var target = event.target;
			if (!(target instanceof Element)) return;

			if (target.closest('[data-quick-view-close]')) {
				event.preventDefault();
				close();
				return;
			}

			var thumb = target.closest('[data-quick-view-thumb]');
			if (thumb) {
				showImage(thumb.getAttribute('data-src'), thumb);
				return;
			}

			// "Color: Rock Black" follows the click even when that combination doesn't exist
			var option = target.closest('[data-option-value]');
			if (option) {
				var group = option.closest('[data-option-group]');
				var selected = group && body.querySelector('[data-quick-view-selected="' + group.getAttribute('data-option-position') + '"]');
				if (selected) selected.textContent = option.getAttribute('data-option-value');
				return;
			}

			if (target.closest('[data-quick-view-retry]')) {
				event.preventDefault();
				load(currentHandle, true);
			}
		});

		// assets/product-buy-box.js: the shopper picked another variant
		modal.addEventListener('product:variant-change', function (event) {
			var variant = event.detail ? event.detail.variant : null;

			var stock = body.querySelector('[data-quick-view-stock]');
			if (stock) {
				stock.textContent = !variant ? 'Unavailable' : variant.available ? 'In stock' : 'Out of stock';
				stock.classList.toggle('is-out', !variant || !variant.available);
			}

			if (variant && variant.image) showImage(variant.image);

			var product = body.querySelector('[data-quick-view-product]');
			var details = body.querySelector('[data-quick-view-details]');
			if (product && details) {
				details.href = product.getAttribute('data-product-url') + (variant ? '?variant=' + variant.id : '');
			}
		});
	}

	// The same image can come as "/cdn/shop/files/x.webp?…" (an attribute) or with the shop's
	// domain in front (the variants JSON), so compare without the host.
	function samePicture(a, b) {
		var strip = function (url) {
			return String(url || '').replace(/^(https?:)?\/\/[^/]+/, '');
		};
		return strip(a) === strip(b);
	}

	function showImage(src, thumb) {
		var image = body.querySelector('[data-quick-view-image]');
		if (!image || !src) return;
		if (!samePicture(image.getAttribute('src'), src)) image.setAttribute('src', src);
		all(body, '[data-quick-view-thumb]').forEach(function (button) {
			var on = thumb ? button === thumb : samePicture(button.getAttribute('data-src'), src);
			button.classList.toggle('is-active', on);
			button.setAttribute('aria-pressed', on ? 'true' : 'false');
		});
	}

	function showLoading() {
		modal.removeAttribute('data-loaded');
		body.innerHTML =
			'<div class="be-qv-modal__status" role="status">' +
			'<span class="be-qv-modal__spinner" aria-hidden="true"></span>' +
			'<span>Loading…</span>' +
			'</div>';
	}

	function showMessage(message, canRetry) {
		modal.removeAttribute('data-loaded');
		body.textContent = '';

		var box = document.createElement('div');
		box.className = 'be-qv-modal__status';
		box.setAttribute('role', 'alert');

		var text = document.createElement('p');
		text.textContent = message;
		box.appendChild(text);

		var actions = document.createElement('div');
		actions.className = 'be-qv-modal__status-actions';
		if (canRetry) {
			var retry = document.createElement('button');
			retry.type = 'button';
			retry.className = 'be-qv__choice';
			retry.setAttribute('data-quick-view-retry', '');
			retry.textContent = 'Try again';
			actions.appendChild(retry);
		}
		var link = document.createElement('a');
		link.className = 'be-qv__details';
		link.href = shopRoot + 'products/' + encodeURIComponent(currentHandle);
		link.textContent = 'Open the product page';
		actions.appendChild(link);
		box.appendChild(actions);

		body.appendChild(box);
	}

	/* ───────────────────────── content ───────────────────────── */

	function load(handle, force) {
		var id = (request += 1);
		var cached = cache[handle];
		if (!force && cached && Date.now() - cached.at < CACHE_MS) {
			render(cached.html);
			return;
		}

		showLoading();
		// No `Accept: application/json` header: on a product URL Shopify answers that with the
		// product's JSON and ignores ?sections= entirely.
		fetch(shopRoot + 'products/' + encodeURIComponent(handle) + '?sections=' + SECTION)
			.then(function (response) {
				if (response.status === 404) return null;
				if (!response.ok) throw new Error('HTTP ' + response.status);
				return response.json().then(function (data) {
					return (data && data[SECTION]) || null;
				});
			})
			.then(function (html) {
				if (id !== request) return; // closed, or another product opened meanwhile
				if (!html) {
					showMessage('This product is no longer available.', false);
					return;
				}
				cache[handle] = { html: html, at: Date.now() };
				render(html);
			})
			.catch(function () {
				if (id !== request) return;
				showMessage('We could not load this product.', true);
			});
	}

	function render(html) {
		var product = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-view-product]');
		if (!product) {
			showMessage('This product is no longer available.', false);
			return;
		}

		body.textContent = '';
		body.appendChild(document.adoptNode(product));
		body.scrollTop = 0;
		modal.setAttribute('data-loaded', '');

		if (body.querySelector('#QuickViewTitle')) {
			dialog.setAttribute('aria-labelledby', 'QuickViewTitle');
			dialog.removeAttribute('aria-label');
		}
		if (window.BEProductBuyBox) window.BEProductBuyBox.init(product);
	}

	/* ───────────────────────── open / close ───────────────────────── */

	function open(handle, opener) {
		if (!handle) return;
		build();

		lastOpener = opener instanceof Element ? opener : document.activeElement;
		currentHandle = handle;
		dialog.setAttribute('aria-label', 'Quick view');
		dialog.removeAttribute('aria-labelledby');

		if (!modal.hasAttribute('data-open')) {
			modal.removeAttribute('inert');
			modal.setAttribute('aria-hidden', 'false');
			void modal.offsetWidth; // start the fade from the closed state
			modal.setAttribute('data-open', '');
			if (window.BuyElectro && window.BuyElectro.lockScroll) window.BuyElectro.lockScroll('quick-view');
			document.addEventListener('keydown', onKeydown);
		}

		load(handle, false);
		window.requestAnimationFrame(function () {
			dialog.focus({ preventScroll: true });
		});
	}

	function close() {
		if (!modal || !modal.hasAttribute('data-open')) return;

		request += 1; // a response still on its way is ignored
		modal.removeAttribute('data-open');
		modal.setAttribute('aria-hidden', 'true');
		modal.setAttribute('inert', '');
		document.removeEventListener('keydown', onKeydown);
		if (window.BuyElectro && window.BuyElectro.unlockScroll) window.BuyElectro.unlockScroll('quick-view');

		var opener = lastOpener;
		lastOpener = null;
		if (opener && opener !== document.body && document.contains(opener) && typeof opener.focus === 'function') {
			opener.focus({ preventScroll: true });
		}

		window.setTimeout(function () {
			if (!modal.hasAttribute('data-open')) body.textContent = '';
		}, 250);
	}

	function cartIsOpen() {
		return !!document.querySelector('[data-cart-sidebar][data-open]');
	}

	function onKeydown(event) {
		// Adding to the cart opens the cart panel over the popup; that panel handles its own keys.
		if (cartIsOpen()) return;

		if (event.key === 'Escape') {
			event.preventDefault();
			close();
			return;
		}
		if (event.key !== 'Tab') return;

		var focusables = all(dialog, 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]').filter(function (node) {
			return node.getAttribute('tabindex') !== '-1' && node.getClientRects().length > 0;
		});
		if (!focusables.length) {
			event.preventDefault();
			dialog.focus();
			return;
		}
		var first = focusables[0];
		var last = focusables[focusables.length - 1];
		if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	/* ───────────────────────── triggers ───────────────────────── */

	document.addEventListener('click', function (event) {
		var target = event.target;
		if (!(target instanceof Element)) return;
		var trigger = target.closest('[data-quick-view]');
		if (!trigger || (modal && modal.contains(trigger))) return;
		var handle = trigger.getAttribute('data-product-handle');
		if (!handle) return;
		event.preventDefault();
		open(handle, trigger);
	});

	// The card buttons are <span role="button">.
	document.addEventListener('keydown', function (event) {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		var target = event.target;
		if (!(target instanceof Element) || !target.matches('[data-quick-view][role="button"]')) return;
		event.preventDefault();
		target.click();
	});

	window.BEQuickView = { open: open, close: close };
})();
